import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  PhoneNumberInput,
  TextArea,
  TextInput,
} from 'shared/components';
import { isFeatureEnabled } from 'shared/helpers';
import { platformFeatures } from 'shared/constants';
import {
  debouncedValidateEmail,
  debouncedValidateJSON,
  validateEmail,
  validateJSON,
} from 'shared/validation';
import { addAccountManager } from '../../../actions';
import './styles.scss';

const AddAccountManagerModal = ({ accountId, closeCb, confirmCb }) => {
  const { t } = useTranslation();
  const isMetadataFeatureEnabled = isFeatureEnabled(
    platformFeatures.extra_metadata,
  );

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [metadata, setMetadata] = useState('');
  const [metadataError, setMetadataError] = useState('');

  const validateForm = async () => {
    const emailErr = await validateEmail(email);
    setEmailError(emailErr);

    let metadataErr = '';
    if (isMetadataFeatureEnabled) {
      metadataErr = validateJSON(metadata);
      setMetadataError(metadataErr);
    } else {
      setMetadataError('');
    }

    return !emailErr && !metadataErr;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isLoading) {
      return false;
    }

    const isValid = await validateForm();
    if (!isValid) {
      return false;
    }

    setLoading(true);
    const data = {
      email,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      phone_number: phoneNumber || undefined,
      metadata: isMetadataFeatureEnabled
        ? metadata
          ? JSON.parse(metadata)
          : {}
        : undefined,
    };

    return addAccountManager(accountId, data)
      .then(() => {
        setLoading(false);
        Notification('success', t('Changes saved successfully'));
        confirmCb();
        return true;
      })
      .catch(() => {
        setLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
        return false;
      });
  };

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={t('Add license manager')}
      size="md"
    >
      <form className="AddAccountManagerModal" onSubmit={handleSubmit}>
        <div className="form-row">
          <Label text={t('Email')} inputId="account-manager-email" />
          <TextInput
            id="account-manager-email"
            value={email}
            error={emailError}
            handleChange={(val) => {
              setDirty(true);
              setEmail(val);
              debouncedValidateEmail(val).then((err) => setEmailError(err));
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('First Name')} inputId="account-manager-firstname" />
          <TextInput
            id="account-manager-firstname"
            value={firstName}
            handleChange={(val) => {
              setDirty(true);
              setFirstName(val);
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('Last Name')} inputId="account-manager-lastname" />
          <TextInput
            id="account-manager-lastname"
            value={lastName}
            handleChange={(val) => {
              setDirty(true);
              setLastName(val);
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('Phone Number')} inputId="account-manager-phone" />
          <PhoneNumberInput
            value={phoneNumber}
            handleChange={(val) => {
              setDirty(true);
              setPhoneNumber(val || '');
            }}
          />
        </div>
        <div className="form-row">
          <Label text={t('Metadata JSON')} inputId="account-manager-metadata" />
          <TextArea
            id="account-manager-metadata"
            rows="4"
            value={metadata}
            error={metadataError}
            disabled={!isMetadataFeatureEnabled}
            handleChange={(val) => {
              setDirty(true);
              setMetadata(val);
              debouncedValidateJSON(val).then((err) => setMetadataError(err));
            }}
          />
        </div>
        <button type="submit" style={{ display: 'none' }} />
      </form>
      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

AddAccountManagerModal.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default AddAccountManagerModal;
